import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import configs from "../../configs";
import { useHistory } from "react-router-dom";

const ProjectElementsContext = React.createContext();

const ROOT = "verse";

export const ProjectElementsProvider = ({ children, api, isAuthenticated, setIsAuthenticated }) => {
  const [projects, setProjects] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsInFolder, setProjectsInFolder] = useState([]);
  const [folderProjects, setProjectsFolder] = useState([]); // for projects of the currently viewed folder

  // Fetch all projects, folders, and scenes, and validate folder.projects
  const fetchProjectsAndFolders = () => {
    setLoading(true);
    Promise.all([api.getProjects(), api.getFolders(ROOT)])
      .then(([projectsData, foldersDataFromApi]) => {
        const processedProjects = projectsData.map(project => ({
          ...project,
          url: `/projects/${project.project_key}`
        }));
        const processedScenes = [];

        setProjects(processedProjects);
        setScenes(processedScenes);

        const validProjectIds = new Set(processedProjects.map(p => p.project_id));
        const validSceneIds = new Set(processedScenes.map(s => s.id));

        setFolders(
          foldersDataFromApi
            ? foldersDataFromApi.map(folder => {
                let validatedFolderProjects = [];
                if (folder.projects && Array.isArray(folder.projects)) {
                  validatedFolderProjects = folder.projects.filter(item => {
                    const itemId = item.project_id;
                    return validProjectIds.has(itemId) || validSceneIds.has(itemId);
                  });
                }
                return {
                  ...folder,
                  projects: validatedFolderProjects,
                  url: `/collections/${folder.name}`
                };
              })
            : []
        );
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        // Do not hard-logout on initial 401; allow API layer to handle reauth/retry.
        setProjects([]);
        setScenes([]);
        setFolders([]);
        setError(err);
        setLoading(false);
      });
  };

  // Fetch projects, scenes, and a specific folder, validating folder.projects
  const fetchProjectAndCurrentFolder = folderPath => {
    const queryParams = new URLSearchParams(window.location.search);
    const root = queryParams.get("root");

    if (isAuthenticated) {
      setLoading(true);
      Promise.all([api.getProjects(), api.getFolderProjects(folderPath), api.getFolders(ROOT)])
        .then(([projectsData, currentFolderDataFromApi, allRootFoldersData]) => {
          const processedProjects = projectsData.map(project => ({
            ...project,
            url: `/projects/${project.project_key}`
          }));
          const processedScenes = [];

          setProjects(processedProjects);
          setScenes(processedScenes);

          const validProjectIds = new Set(processedProjects.map(p => p.project_id));
          const validSceneIds = new Set(processedScenes.map(s => s.id));

          const currentFolderObject = Array.isArray(currentFolderDataFromApi)
            ? root
              ? currentFolderDataFromApi.find(folder => folder.root == root)
              : currentFolderDataFromApi[0]
            : currentFolderDataFromApi;

          let validatedItemsForCurrentFolder = [];
          if (currentFolderObject && currentFolderObject.projects && Array.isArray(currentFolderObject.projects)) {
            validatedItemsForCurrentFolder = currentFolderObject.projects.filter(item => {
              const itemId = item.project_id;
              return validProjectIds.has(itemId) || validSceneIds.has(itemId);
            });
          }

          setProjectsFolder(validatedItemsForCurrentFolder);
          const projectIdsInValidatedFolder = validatedItemsForCurrentFolder.map(item => item.project_id);
          setProjectsInFolder(projectIdsInValidatedFolder);

          if (!folders?.length && allRootFoldersData) {
            setFolders(
              allRootFoldersData.map(folder => {
                let validatedFolderProjectsList = [];
                if (folder.projects && Array.isArray(folder.projects)) {
                  validatedFolderProjectsList = folder.projects.filter(item => {
                    const itemId = item.project_id;
                    return validProjectIds.has(itemId) || validSceneIds.has(itemId);
                  });
                }
                return {
                  ...folder,
                  projects: validatedFolderProjectsList,
                  url: `/collections/${folder.name}`
                };
              })
            );
          } else if (folders?.length) {
            setFolders(prevFolders =>
              prevFolders.map(folderInState => {
                let updatedFolderProjectsList;
                if (currentFolderObject && folderInState.id === currentFolderObject.id) {
                  updatedFolderProjectsList = validatedItemsForCurrentFolder;
                } else {
                  updatedFolderProjectsList = [];
                  if (folderInState.projects && Array.isArray(folderInState.projects)) {
                    updatedFolderProjectsList = folderInState.projects.filter(item => {
                      const itemId = item.project_id;
                      return validProjectIds.has(itemId) || validSceneIds.has(itemId);
                    });
                  }
                }
                return {
                  ...folderInState,
                  projects: updatedFolderProjectsList
                };
              })
            );
          }
          setLoading(false);
        })
        .catch(error => {
          console.error(error);
          if (isAuthenticated && error?.originalError?.response && error?.originalError?.response?.status === 401) {
            api.logout();
            window.location.reload();
          }
          setProjects([]);
          setScenes([]);
          setFolders([]);
          setError(error);
          setLoading(false);
        });
    }
  };

  const updateFolders = (items, foldersToUpdate) => {
    setFolders(currentFolders => {
      const validProjectIds = new Set(projects.map(p => p.project_id));
      const validSceneIds = new Set(scenes.map(s => s.id));
      return currentFolders.map(f => {
        const folderIds = foldersToUpdate.map(fu => fu.id);
        if (folderIds.includes(f.id)) {
          const existingIds = new Set(f.projects.map(p => p.project_id));
          const newItems = items.filter(item => {
            const itemId = item.project_id;
            return (validProjectIds.has(itemId) || validSceneIds.has(itemId)) && !existingIds.has(itemId);
          });
          return {
            ...f,
            projects: [...f.projects, ...newItems]
          };
        }
        return f;
      });
    });
  };

  const removeFromCollectionFolder = (folderId, projectsToRemove) => {
    setFolders(folders =>
      folders.map(folder => {
        if (folder.id === folderId) {
          return {
            ...folder,
            projects: folder.projects.filter(
              project => !projectsToRemove.some(removeProject => removeProject.project_id === project.project_id)
            )
          };
        }
        return folder;
      })
    );
  };

  useEffect(() => {
    window.addEventListener("metaverse-authentication-refreshed", fetchProjectsAndFolders);
    return () => {
      window.removeEventListener("metaverse-authentication-refreshed", fetchProjectsAndFolders);
    };
  }, []);

  const onExportProject = project => {
    api.exportProject(project);
  };

  // Context value to be provided
  const value = {
    projects,
    scenes,
    folders,
    loading,
    error,
    setLoading,
    setProjects,
    setScenes,
    setFolders,
    fetchProjectsAndFolders,
    fetchProjectAndCurrentFolder,
    setProjectsInFolder,
    projectsInFolder,
    folderProjects,
    isAuthenticated,
    setIsAuthenticated,
    updateFolders,
    removeFromCollectionFolder,
    onExportProject
  };

  return <ProjectElementsContext.Provider value={value}>{children}</ProjectElementsContext.Provider>;
};

// Custom hook to use the Project Elements context
export const useProjectElements = () => useContext(ProjectElementsContext);

ProjectElementsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  api: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  setIsAuthenticated: PropTypes.func.isRequired
};
